import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Activity, Database, AlertTriangle, GitBranch, ClipboardList, LogOut, LayoutDashboard, Bell, Zap, Users, FolderOpen, Plug, Shield, Target, Grid3X3, Radio, Search, BarChart3, FileText, Layers, LayoutGrid, BookOpen, FlaskConical, Settings } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent } from "../ui/dialog";
import { Input } from "../ui/input";

const navGroups = [
  { title: "Infrastructure", items: [
    { to: "/", icon: LayoutDashboard, label: "Overview", exact: true },
    { to: "/hostmap", icon: Grid3X3, label: "Host Map" },
    { to: "/pipelines", icon: Database, label: "Pipelines" },
  ]},
  { title: "Observability", items: [
    { to: "/metrics", icon: BarChart3, label: "Metrics" },
    { to: "/logs", icon: FileText, label: "Logs" },
    { to: "/traces", icon: Layers, label: "Traces" },
  ]},
  { title: "Reliability", items: [
    { to: "/incidents", icon: AlertTriangle, label: "Incidents" },
    { to: "/monitors", icon: Shield, label: "Monitors" },
    { to: "/slos", icon: Target, label: "SLOs" },
    { to: "/synthetics", icon: FlaskConical, label: "Synthetics" },
  ]},
  { title: "Explore", items: [
    { to: "/dashboards", icon: LayoutGrid, label: "Dashboards" },
    { to: "/lineage", icon: GitBranch, label: "Service Map" },
    { to: "/events", icon: Radio, label: "Events" },
    { to: "/notebooks", icon: BookOpen, label: "Notebooks" },
    { to: "/audit", icon: ClipboardList, label: "Audit Log" },
  ]},
  { title: "Admin", items: [
    { to: "/settings/users", icon: Users, label: "Users" },
    { to: "/settings/projects", icon: FolderOpen, label: "Projects" },
    { to: "/settings/integrations", icon: Plug, label: "Integrations" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ]},
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [liveEvents, setLiveEvents] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const isViewer = user?.role === "viewer";

  useEffect(() => { api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const handler = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setShowSearch(true); } };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setSearchResults(data.results || []); } catch { setSearchResults([]); }
  }, []);

  const handleSearchSelect = (result) => { setShowSearch(false); setSearchQuery(""); setSearchResults([]); navigate(result.url); };

  useEffect(() => {
    const interval = setInterval(async () => {
      try { if (!isViewer) { await api.post("/simulate-event"); setLiveEvents((p) => p + 1); const { data } = await api.get("/alerts"); setAlerts(data); } } catch (err) { console.error(err); }
    }, 30000);
    return () => clearInterval(interval);
  }, [isViewer]);

  const unreadAlerts = alerts.filter((a) => a.severity !== "info").length;

  return (
    <div className="flex h-screen overflow-hidden bg-[#FAF9F6]" data-testid="app-layout">
      {/* Sidebar — Warm Light */}
      <aside className="w-[250px] flex-shrink-0 flex flex-col bg-white border-r border-[#E8E4DF]" data-testid="sidebar">
        <div className="px-5 py-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#1E3A5F]">
            <Activity className="w-[18px] h-[18px] text-white" />
          </div>
          <div>
            <h1 className="text-[13px] font-extrabold tracking-tight text-[#2D3142]" data-testid="app-title">Pipeline Doctor</h1>
            <p className="text-[9px] tracking-[0.15em] uppercase font-semibold text-[#1E3A5F]/50">Control Tower</p>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 mb-2">
          <button onClick={() => setShowSearch(true)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] transition-colors bg-[#FAF9F6] border border-[#E8E4DF] text-[#9CA3AF] hover:border-[#D0CCC6]" data-testid="search-trigger">
            <Search className="w-3.5 h-3.5" /><span className="flex-1 text-left">Search...</span>
            <kbd className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#9CA3AF]">Ctrl+K</kbd>
          </button>
        </div>

        {isViewer && (
          <div className="mx-4 mb-1 px-3 py-2 rounded-lg bg-[#E76F51]/[0.06] border border-[#E76F51]/15" data-testid="viewer-banner">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#E76F51]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#E76F51]">Demo Mode</span>
            </div>
            <p className="text-[9px] mt-0.5 text-[#E76F51]/60">Read-only access</p>
          </div>
        )}

        <ScrollArea className="flex-1 px-3 py-2">
          {navGroups.map((group) => (
            <div key={group.title} className="mb-3">
              <p className="text-[9px] uppercase tracking-[0.2em] font-bold px-3 mb-1.5 text-[#9CA3AF]">{group.title}</p>
              <nav className="space-y-0.5" data-testid="sidebar-nav">
                {group.items.map((item) => {
                  const isActive = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
                  return (
                    <NavLink key={item.to} to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-[#1E3A5F]/[0.06] text-[#1E3A5F] shadow-sm"
                          : "text-[#6B7280] hover:text-[#2D3142] hover:bg-[#FAF9F6]"
                      }`}>
                      <item.icon className={`w-4 h-4 ${isActive ? "text-[#1E3A5F]" : ""}`} />
                      <span>{item.label}</span>
                      {item.label === "Incidents" && unreadAlerts > 0 && (
                        <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center text-white bg-[#BE3455]">{unreadAlerts}</span>
                      )}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          ))}

          {liveEvents > 0 && (
            <div className="mx-1 px-3 py-2.5 rounded-lg bg-[#2D6A4F]/[0.06] border border-[#2D6A4F]/12" data-testid="live-events">
              <div className="flex items-center gap-1.5">
                <Zap className="w-3 h-3 text-[#2D6A4F]" />
                <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#2D6A4F]">Streaming</span>
              </div>
              <p className="text-[10px] font-mono mt-0.5 text-[#2D6A4F]/60">{liveEvents} events ingested</p>
            </div>
          )}
        </ScrollArea>

        <div className="px-4 py-4 space-y-3 border-t border-[#E8E4DF]">
          <div className="flex items-center justify-between">
            <button onClick={() => setShowAlerts(true)} className="relative p-1.5 rounded-lg transition-colors text-[#6B7280] hover:bg-[#FAF9F6]" data-testid="alerts-bell">
              <Bell className="w-4 h-4" />
              {unreadAlerts > 0 && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 bg-[#BE3455] border-white" />}
            </button>
            <Badge className="text-[9px] font-mono border-0 rounded-md" style={isViewer ? { background: "rgba(231,111,81,0.1)", color: "#E76F51" } : { background: "rgba(30,58,95,0.08)", color: "#1E3A5F" }}>{user?.role || "viewer"}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <div className="truncate">
              <p className="text-[12px] font-medium text-[#2D3142] truncate">{user?.name || "User"}</p>
              <p className="text-[10px] text-[#9CA3AF] truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-[#9CA3AF] hover:text-[#BE3455] hover:bg-[#BE3455]/10" onClick={logout} data-testid="logout-button">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto sf-gradient-bg">
        <Outlet />
      </main>

      {/* Alerts Sheet */}
      <Sheet open={showAlerts} onOpenChange={setShowAlerts}>
        <SheetContent className="w-full sm:max-w-[400px] overflow-auto bg-white border-l border-[#E8E4DF]" data-testid="alerts-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-[#2D3142]">
              <Bell className="w-4 h-4 text-[#1E3A5F]" /> Notifications
              <Badge className="text-[9px] font-mono ml-auto border-0 bg-[#1E3A5F]/8 text-[#1E3A5F]">{alerts.length}</Badge>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {alerts.map((a) => (
              <div key={a.alert_id} className="p-3 rounded-lg text-sm border" style={{ background: a.severity === "critical" ? "rgba(190,52,85,0.04)" : a.severity === "high" ? "rgba(231,111,81,0.04)" : "#FAF9F6", borderColor: a.severity === "critical" ? "rgba(190,52,85,0.15)" : a.severity === "high" ? "rgba(231,111,81,0.15)" : "#E8E4DF" }} data-testid={`alert-${a.alert_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <Badge className="text-[8px] uppercase border-0" style={{ background: a.severity === "critical" ? "rgba(190,52,85,0.1)" : a.severity === "high" ? "rgba(231,111,81,0.1)" : "rgba(45,49,66,0.06)", color: a.severity === "critical" ? "#BE3455" : a.severity === "high" ? "#E76F51" : "#6B7280" }}>{a.severity}</Badge>
                  <span className="text-[9px] font-mono text-[#9CA3AF]">{a.channel === "slack" ? "#slack" : "#teams"}</span>
                </div>
                <p className="text-[12px] leading-relaxed text-[#6B7280]">{a.message}</p>
                <p className="text-[9px] font-mono mt-1 text-[#9CA3AF]">{a.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
            {alerts.length === 0 && <p className="text-center text-sm py-8 font-mono text-[#9CA3AF]">[ No notifications ]</p>}
          </div>
        </SheetContent>
      </Sheet>

      {/* Search Overlay */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden bg-white border border-[#E8E4DF] shadow-xl" data-testid="search-dialog">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E8E4DF]">
            <Search className="w-4 h-4 text-[#1E3A5F]" />
            <Input value={searchQuery} onChange={(e) => doSearch(e.target.value)} placeholder="Search pipelines, incidents, projects..." className="border-0 bg-transparent text-[#2D3142] placeholder:text-[#9CA3AF] focus-visible:ring-0 h-8 text-[14px]" autoFocus data-testid="search-input" />
          </div>
          {searchResults.length > 0 && (
            <div className="max-h-[300px] overflow-auto">
              {searchResults.map((r) => (
                <button key={`${r.type}-${r.id}`} onClick={() => handleSearchSelect(r)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FAF9F6] transition-colors border-b border-[#F3F1EC]" data-testid={`search-result-${r.id}`}>
                  <Badge className="text-[8px] uppercase font-bold border-0 rounded-md min-w-[60px] text-center" style={{ background: r.type === "pipeline" ? "rgba(30,58,95,0.08)" : r.type === "incident" ? "rgba(190,52,85,0.08)" : "rgba(124,58,237,0.08)", color: r.type === "pipeline" ? "#1E3A5F" : r.type === "incident" ? "#BE3455" : "#7C3AED" }}>{r.type}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-[#2D3142] truncate">{r.title}</p>
                    <p className="text-[10px] truncate text-[#9CA3AF]">{r.subtitle}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {searchQuery.length >= 2 && searchResults.length === 0 && <div className="px-4 py-8 text-center text-[12px] font-mono text-[#9CA3AF]">No results for "{searchQuery}"</div>}
          {searchQuery.length < 2 && <div className="px-4 py-6 text-center text-[11px] text-[#9CA3AF]">Type to search pipelines, incidents, and projects</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
